import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../../services/apiService';
import '../MetasConfig.css';
import './Escalas.css';
import { brDate, mensagemErro } from './escalasUtil';

const dataCurta = (v) => (v ? brDate(String(v).split('T')[0]) : '—');
const numero = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v));

/**
 * Painel "Congelamento do passado": o estado do fechamento dos dias da empresa e a conferência entre a
 * regra e o congelado. Só leitura. O critério para o congelado sair é 3 meses seguidos sem divergência
 * INEXPLICADA, por isso essa contagem é o número que importa aqui. Carrega ao abrir (não a cada visita
 * à aba) e tolera API sem a rota: mostra o aviso em vez de quebrar o Calendário.
 */
function CongelamentoPanel({ subdomain, notify }) {
    const [aberto, setAberto] = useState(false);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [indisponivel, setIndisponivel] = useState('');
    const seq = useRef(0);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        const minha = ++seq.current;
        setCarregando(true);
        setIndisponivel('');
        try {
            const r = await apiService.scheduleFreezeStatus({ subdomain });
            if (!r || r.success === false || typeof r !== 'object') throw new Error('resposta fora de forma');
            if (minha === seq.current) setDados(r);
        } catch (e) {
            if (minha === seq.current) {
                setDados(null);
                setIndisponivel(mensagemErro(e, 'O estado do congelamento ainda não está disponível neste ambiente.'));
            }
        } finally {
            if (minha === seq.current) setCarregando(false);
        }
    }, [subdomain]);

    // Trocou de empresa: esquece o que tinha e fecha; só consulta quando a pessoa abre.
    useEffect(() => { seq.current += 1; setDados(null); setIndisponivel(''); setAberto(false); }, [subdomain]);

    const alternar = () => {
        const vaiAbrir = !aberto;
        setAberto(vaiAbrir);
        if (vaiAbrir && !dados && !carregando) carregar();
    };

    const drift = (dados && dados.drift) || {};
    const itens = Array.isArray(dados && dados.items) ? dados.items : [];
    const inexplicadas = drift.unexplained_90d;
    const resumo = !dados
        ? 'ainda não consultado'
        : dados.started_on
            ? `desde ${dataCurta(dados.started_on)} · ${numero(inexplicadas)} divergência(s) inexplicada(s) em 90 dias`
            : 'ainda não iniciado nesta empresa';

    return (
        <div className="mc-form-panel esc-ajuste esc-ajuste-mini esc-cong-painel">
            <button
                type="button"
                className="esc-ajuste-cab"
                onClick={alternar}
                aria-expanded={aberto}
                aria-controls="esc-cong-corpo"
            >
                <span aria-hidden="true">{aberto ? '▾' : '▸'}</span>
                <strong>Congelamento do passado</strong>
                <small>{carregando ? 'consultando…' : resumo}</small>
            </button>

            {aberto && (
                <div id="esc-cong-corpo">
                    <p className="esc-dica">
                        Dia fechado é o anterior ao primeiro dia aberto. Cada um é gravado uma vez e só muda por revisão, com
                        motivo. A conferência recalcula o dia ao vivo e compara com o congelado; o congelado só sai quando houver
                        3 meses seguidos sem divergência inexplicada.
                    </p>
                    {indisponivel && <p className="esc-aviso" role="status">{indisponivel}</p>}
                    {dados && (
                        <>
                            <div className="esc-cong-resumo">
                                <span>Início do congelamento: <strong>{dataCurta(dados.started_on)}</strong></span>
                                <span>Último dia fechado: <strong>{dataCurta(dados.last_closed_day)}</strong></span>
                                <span>Primeiro dia aberto: <strong>{dataCurta(dados.first_open_day)}</strong></span>
                                <span>Dias sem registro: <strong>{numero(dados.days_without_row)}</strong></span>
                                <span>Inexplicadas (90 dias): <strong className={Number(inexplicadas) > 0 ? 'esc-cong-inex' : undefined}>{numero(inexplicadas)}</strong></span>
                                <span>Explicadas (90 dias): <strong>{numero(drift.explained_90d)}</strong></span>
                                {drift.last_unexplained_at && <span>Última inexplicada: <strong>{dataCurta(drift.last_unexplained_at)}</strong></span>}
                            </div>
                            {itens.length === 0
                                ? <p className="esc-dica">Nenhuma divergência registrada.</p>
                                : (
                                    <div className="esc-cong-itens" role="list" aria-label="Divergências recentes">
                                        {itens.slice(0, 50).map((i) => (
                                            <div key={i.id} className="esc-cong-item" role="listitem">
                                                <strong>{dataCurta(i.day)}</strong>
                                                <span>vendedor {i.salesman_id}</span>
                                                <span className={i.kind === 'unexplained' ? 'esc-cong-inex' : 'esc-cong-expl'}>
                                                    {i.kind === 'unexplained' ? 'inexplicada' : 'explicada'}
                                                </span>
                                                <span>{i.cause || '—'}</span>
                                                <small>revisão {numero(i.frozen_revision)} · resolvedor {i.resolver_version || '—'}</small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            {itens.length > 50 && <p className="esc-dica">Mostrando 50 de {itens.length}.</p>}
                        </>
                    )}
                    <div className="mc-form-actions">
                        <button className="mc-btn mc-btn-sm" onClick={carregar} disabled={carregando || !subdomain}>
                            {carregando ? 'Consultando…' : 'Atualizar'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CongelamentoPanel;
